/**
 * Fees / Raw + MM Rebate display toggles.
 *
 * Both flip global `displaySettings`, so they affect every odds/fee surface
 * app-wide — dashboard, game detail, and the trade tickets/modals. The MM
 * Rebate button only renders when the trader's org is enrolled in Kalshi's
 * Market Maker Program (AuthUser.mmRebateEnabled). Shared by the value
 * dashboard and the game-detail page so the controls stay identical.
 *
 * See [[project_kalshi_mm_rebate_model]].
 */
import { cn } from '@/lib/infra/utils';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useAuthStore } from '@/stores/authStore';

export function OddsFeeToggles({ className }: { className?: string }) {
  const feeAdjustedOdds = useDisplaySettings((s) => s.feeAdjustedOdds);
  const toggleFeeAdjustedOdds = useDisplaySettings((s) => s.toggleFeeAdjustedOdds);
  const makerRebateEnabled = useDisplaySettings((s) => s.makerRebateEnabled);
  const toggleMakerRebate = useDisplaySettings((s) => s.toggleMakerRebate);
  const orgMmEnrolled = useAuthStore((s) => s.user?.mmRebateEnabled ?? false);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Fees / Raw odds toggle. Default: Fees on (maker rate), since the
          typical workflow is bidding (maker). Click to compare against raw. */}
      <button
        onClick={toggleFeeAdjustedOdds}
        title={
          feeAdjustedOdds
            ? 'Showing fee-adjusted (maker) odds — click for raw'
            : 'Showing raw odds — click for fee-adjusted (maker)'
        }
        className={cn(
          'rounded-md border px-2 py-1 font-mono text-[11px] transition-colors',
          feeAdjustedOdds
            ? 'border-primary text-primary'
            : 'border-border text-muted-foreground hover:text-foreground',
        )}
      >
        {feeAdjustedOdds ? 'Fees' : 'Raw'}
      </button>

      {/* MM Rebate toggle — folds the Market Maker Program rebate (50%
          pregame / 100% live reduction of the maker fee) into maker-side fee
          + odds. Only shown when the org is enrolled; a no-op when Fees is off
          (nothing to reduce) or in taker/market perspective. */}
      {orgMmEnrolled && (
        <button
          onClick={toggleMakerRebate}
          title={
            makerRebateEnabled
              ? 'MM rebate folded into maker fees (50% pregame / 100% live) — click to show gross maker fees'
              : 'Showing gross maker fees — click to fold in the MM Program rebate'
          }
          className={cn(
            'rounded-md border px-2 py-1 font-mono text-[11px] transition-colors',
            makerRebateEnabled
              ? 'border-emerald-500 text-emerald-500'
              : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          MM Rebate
        </button>
      )}
    </div>
  );
}
